/*
 * Above 9 · Tranquitone · The Ritual Guide comes with every set and every fork (original work, 2026-10-09;
 * DECISIONS.md row 57).
 *
 * The guide is its own $0 product in the Digital Products app, which delivers it on the thank-you page and in the
 * confirmation email for any order that holds it. This script makes sure such an order holds it: on every page load
 * and after every cart change (Shopify's standard 'shopify:cart:lines-update' event, which Horizon and our add-on
 * scripts both dispatch), it reads the cart; when a line's product type is one of the configured types (a set or a
 * single fork) and the guide is not in the cart, it adds the guide once (quantity 1, hidden property
 * _a9_source = included), through the same standard event so the drawer and the cart page re-render. Its own add
 * re-fires the event, finds the guide present and stops. A shopper may remove the guide: it is then not re-added
 * until the cart next changes. Config comes from the JSON tag #a9-included (snippets/cart-drawer.liquid); with no
 * guide product, nothing runs.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const config = (() => {
  try {
    const tag = document.getElementById('a9-included');
    return tag ? JSON.parse(tag.textContent || '{}') : {};
  } catch {
    return {};
  }
})();

const GUIDE = Number(config.variant) || 0;
const TYPES = Array.isArray(config.types) ? config.types : [];
let running = false;
let prevHadGuide = false;
let prevSignature = '';
let removedSignature = '';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
  return response.json();
}

async function addGuide() {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent(
      /** @type {any} */ ({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(GUIDE), quantity: 1 }],
        promise: deferred.promise,
      })
    )
  );
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [{ id: GUIDE, quantity: 1, properties: { _a9_source: 'included' } }],
      sections: cartSectionIds(),
    }),
  });
  const result = await response.json();
  const cart = await readCart();
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: Boolean(result?.status) && result?.status !== 200,
      items: cart.items,
      source: 'a9-include-guide',
      sourceId: 'a9-include-guide',
      itemCount: 1,
      sections: result?.sections,
    },
  });
}

/** @param {any[]} items */
function signature(items) {
  return items
    .filter((item) => Number(item.variant_id) !== GUIDE)
    .map((item) => `${item.variant_id}x${item.quantity}`)
    .sort()
    .join(',');
}

async function ensureGuide() {
  if (!GUIDE || TYPES.length === 0 || running) return;
  running = true;
  try {
    const cart = await readCart();
    const items = Array.isArray(cart.items) ? cart.items : [];
    const qualifies = items.some((item) => TYPES.includes(item.product_type));
    const hasGuide = items.some((item) => Number(item.variant_id) === GUIDE);
    const sig = signature(items);
    // The guide was there, the rest of the cart is unchanged, and now it is gone: the shopper removed it. Respect
    // that until the rest of the cart changes.
    if (!hasGuide && prevHadGuide && sig === prevSignature) removedSignature = sig;
    prevHadGuide = hasGuide;
    prevSignature = sig;
    if (!qualifies || hasGuide || sig === removedSignature) return;
    await addGuide();
    prevHadGuide = true;
  } catch {
    // Never block the cart: a failed read or add leaves the cart as the shopper made it.
  } finally {
    running = false;
  }
}

document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  const anyEvent = /** @type {any} */ (event);
  Promise.resolve(anyEvent.promise)
    .catch(() => null)
    .then(() => ensureGuide());
});

ensureGuide();
